import type { Logger } from '@vp/ubik-logging'

import type { QuantitySelectionData } from '../types/QuantitySelectionData'
import type { RelatedProduct, RelatedProductsBffResponse } from '../types/RelatedProduct'
import { REQUESTOR } from '../utils/constants'

const baseUrl = 'https://qsp-bff.large-assortment.vpsvc.com/v1'

export const getQuantitySelectionData = async (
  tenant: string,
  culture: string,
  productKey: string,
  selectedOptions: Record<string, string>,
  logger: Logger,
  productVersion?: number,
  mpvId?: string,
): Promise<QuantitySelectionData> => {
  const formattedSelectedOptions = formatSelectedOptions(selectedOptions)
  const queryParams = buildQueryParams({ mpvId, productVersion: productVersion?.toString() })
  const url = `${baseUrl}/qsp/${tenant}/${culture}/${productKey}/config?${formattedSelectedOptions}&${queryParams.toString()}`

  try {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data = (await response.json()) as QuantitySelectionData
    return data
  } catch (error) {
    logger.error(`Unable to retrieve required quantity selection data from bff for ${productKey}`, error as Error, {
      culture,
      productKey,
      selectedOptions,
      productVersion,
      mpvId,
      url,
    })
    throw error
  }
}

export const getRelatedProductsData = async (
  tenant: string,
  merchantId: string,
  culture: string,
  country: string,
  productKey: string,
  sourceSelectedOptions: Record<string, string>,
  udsDocumentUrl: string,
  logger: Logger,
): Promise<RelatedProduct[]> => {
  const formattedSelectedOptions = formatSelectedOptions(sourceSelectedOptions)
  const queryParams = buildQueryParams({ udsDocumentUrl })
  const url = `${baseUrl}/qsp/${tenant}/${culture}/${productKey}/relatedProducts?${formattedSelectedOptions}&${queryParams.toString()}`

  try {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data = (await response.json()) as RelatedProductsBffResponse[]
    return data.map((relatedProduct) => ({
      ...relatedProduct,
      productInfo: {
        productKey: relatedProduct.productKey,
        productVersion: relatedProduct.productVersion,
        merchantId,
        market: country,
        selections: relatedProduct.selectedOptions,
      },
    }))
  } catch {
    logger.warn(`Unable to retrieve related products data from bff for ${productKey} but that's okay.`, {
      culture,
      country,
      productKey,
      sourceSelectedOptions,
      udsDocumentUrl,
      url,
    })
    return [] as RelatedProduct[]
  }
}

export const formatSelectedOptions = (selectedOptions: Record<string, string>): string => {
  const formattedSelectedOptions = new URLSearchParams()

  // Iterate over the options and manually create the qs-like keys
  for (const [key, value] of Object.entries(selectedOptions)) {
    // Construct the key as 'selectedOptions[Raw Key]'
    const formattedKey = `selectedOptions[${key}]`

    // Append the key-value pair to params.
    // URLSearchParams automatically handles URL encoding (e.g., spaces and #)
    formattedSelectedOptions.append(formattedKey, value)
  }

  return formattedSelectedOptions.toString()
}

export const buildQueryParams = (params: Record<string, string | undefined>): URLSearchParams => {
  const queryParams = new URLSearchParams({
    requestor: REQUESTOR,
  })

  // Add each param to the query parameters
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      queryParams.append(key, value)
    }
  }

  return queryParams
}
